import React, { createContext, useContext } from "react"
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react"
import { XMarkIcon } from "@heroicons/react/24/outline"
import { cn } from "./cn"

// Tailwind + Headless UI replacement for react-bootstrap <Modal>. Keeps the
// same API (show / onHide and Modal.Header/Title/Body/Footer) so call sites
// migrate with minimal change. Transitions follow the existing Popup.js pattern.

const ModalContext = createContext({ onHide: () => {} })

const SIZES = {
  sm: "sm:max-w-sm",
  lg: "sm:max-w-2xl",
  xl: "sm:max-w-4xl",
}

export default function Modal({ show = false, onHide = () => {}, size, className = "", children }) {
  return (
    <ModalContext.Provider value={{ onHide }}>
      <Dialog open={show} onClose={onHide} className="relative z-10">
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <DialogPanel
              transition
              className={cn(
                "relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95",
                SIZES[size] || "sm:max-w-lg",
                className
              )}
            >
              {children}
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </ModalContext.Provider>
  )
}

export function ModalHeader({ closeButton = false, className = "", children, ...props }) {
  const { onHide } = useContext(ModalContext)
  return (
    <div
      className={cn("flex items-start justify-between border-b border-gray-200 px-4 py-3", className)}
      {...props}
    >
      <div className="flex-1">{children}</div>
      {closeButton && (
        <button
          type="button"
          aria-label="Close"
          onClick={onHide}
          className="ml-3 rounded text-gray-400 hover:text-gray-600"
        >
          <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          <span className="sr-only">Close</span>
        </button>
      )}
    </div>
  )
}

export function ModalTitle({ className = "", children, ...props }) {
  return (
    <DialogTitle as="h3" className={cn("text-base font-semibold text-gray-900", className)} {...props}>
      {children}
    </DialogTitle>
  )
}

export function ModalBody({ className = "", children, ...props }) {
  return (
    <div className={cn("px-4 py-4", className)} {...props}>
      {children}
    </div>
  )
}

export function ModalFooter({ className = "", children, ...props }) {
  return (
    <div
      className={cn("flex items-center justify-end gap-2 border-t border-gray-200 px-4 py-3", className)}
      {...props}
    >
      {children}
    </div>
  )
}

Modal.Header = ModalHeader
Modal.Title = ModalTitle
Modal.Body = ModalBody
Modal.Footer = ModalFooter
